import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { StationTagsActions } from './station-tags.actions';
import { StationsRepo } from '../../repositories/station-repo';
import { DEFAULT_OFFSET } from '../../shared/constants';
import { TranslateService } from '@ngx-translate/core';
import { GraphTagList } from '../../../swagger-gen/web-secured/models/graph-tag-list';
import { MappedTagInfo } from '../../shared/types';

export class StationTagsEffect {
  private readonly actions$ = inject(Actions);
  private readonly stationsRepo = inject(StationsRepo);
  private readonly translate = inject(TranslateService);

  search$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StationTagsActions.search),
      mergeMap((param) => {
        return this.fetchData(param.stationUid).pipe(
          map((response) => (response ? this.buildTags(response) : [])),
          map((stationOptions) =>
            StationTagsActions.fetchSuccess({ data: stationOptions })
          ),
          catchError((error) => {
            return of(
              StationTagsActions.fetchFailure({ message: error?.toString() })
            );
          })
        );
      })
    );
  });

  private fetchData(
    stationUid: string
  ): Observable<Record<string, GraphTagList> | null> {
    return this.stationsRepo
      .getStationGroupedTags(stationUid, DEFAULT_OFFSET, 100000, [], {})
      .pipe(
        map((response) => response.list),
        mergeMap((groups) => {
          const groupNames = groups?.map((group: any) => group.name) || [];
          return groupNames.length > 0
            ? this.createTagQueries({
                stationUid: stationUid,
                groupNames: groupNames as string[],
              })
            : of(null);
        })
      );
  }

  private createTagQueries(param: {
    stationUid: string;
    groupNames: string[];
  }): Observable<Record<string, GraphTagList>> {
    const queries$: Record<
      string,
      Observable<GraphTagList>
    > = param.groupNames.reduce((acc, groupName) => {
      acc[groupName] = this.stationsRepo.getStationTagsByGroup(
        param.stationUid,
        DEFAULT_OFFSET,
        100000,
        [
          { key: 'groupName', value: 'GROUP' },
          { key: 'sortFieldName', value: 'NAME' },
          { key: 'sortDirection', value: 'DESC' },
          { key: 'groupValue', value: groupName },
        ],
        {}
      );
      return acc;
    }, {} as Record<string, Observable<GraphTagList>>);

    return forkJoin(queries$);
  }

  buildTags(response: Record<string, GraphTagList>): Array<MappedTagInfo> {
    const mappedTags: MappedTagInfo[] = [];

    for (const [key, value] of Object.entries(response)) {
      if (!value?.list?.length) continue;

      const item: MappedTagInfo[] = value.list?.map((tag: any) => ({
        uid: tag.uid as string,
        displayName: tag.displayName || '',
        unit: tag.unit || '',
        valueType:
          this.translate.instant(
            `export-creation.tags.table.rows.value_type.${tag.valueType}`
          ) || '',
        parent: key,
        checked: false,
      }));

      mappedTags.push(...item);
    }

    return mappedTags;
  }
}
